import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Plus, ClipboardList, Search, Loader, RefreshCw, BookOpen, Calendar } from "lucide-react";
import { lmsGetExams, lmsDeleteExam, lmsGetTeacherByUserId, lmsGetTeacherClassSubjects, lmsGetBatches } from "../api/lmsApi";
import { fetchLmsSchoolClasses, fetchLmsClassSubjects } from "../redux/lmsSlice";
import { ExamModal, ExamCard, ExamDetailDrawer } from "./Exams";
import "../lms.css";

const getTeacherData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

export default function TeacherExams() {
  const dispatch      = useDispatch();
  const nav           = useNavigate();
  const userData      = getTeacherData();
  const adminId       = userData?.admin_id || null;
  const schoolId      = userData?.school_id || userData?.admin_id || null;
  const schoolClasses = useSelector((s) => s.lms?.schoolClasses || []);
  const classSubjects = useSelector((s) => s.lms?.classSubjects || []);

  const [resolvedTeacherId, setResolvedTeacherId] = useState(userData?.teacher_id || null);
  const [teacherClasses,    setTeacherClasses]    = useState(null);
  const [teacherSubjects,   setTeacherSubjects]   = useState(null);
  const [exams,       setExams]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [typeFilter,  setTypeFilter]  = useState("");
  const [batches,     setBatches]     = useState([]);
  const [showModal,   setShowModal]   = useState(false);
  const [editExam,    setEditExam]    = useState(null);
  const [viewExam,    setViewExam]    = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  useEffect(() => {
    setBatchFilter("");
    setBatches([]);
    if (!classFilter) return;
    lmsGetBatches({ class_id: classFilter, school_id: schoolId })
      .then((r) => setBatches(r.data?.data || r.data || []))
      .catch(() => {});
  }, [classFilter, schoolId]);

  const loadExams = () => {
    if (!schoolId) return;
    setLoading(true);
    const params = { school_id: schoolId };
    if (classFilter) params.class_id = classFilter;
    lmsGetExams(params)
      .then((res) => {
        const all = res?.data?.data || [];
        // Filter to teacher's class+subject pairs if available
        if (teacherClasses && teacherClasses.length > 0) {
          const pairs = new Set(
            (teacherSubjects || []).map((s) => `${s.class_id}:${s.subject_id}`)
          );
          const classIds = new Set(teacherClasses.map((c) => String(c.class_id)));
          setExams(all.filter((e) =>
            classIds.has(String(e.class_id)) ||
            pairs.has(`${e.class_id}:${e.subject_id}`)
          ));
        } else {
          setExams(all);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const setupTeacher = (tid) => {
    setResolvedTeacherId(tid);
    lmsGetTeacherClassSubjects(tid)
      .then((res) => {
        const assignments = res?.data?.data || [];
        const seenClasses = new Set();
        const classes = [];
        assignments.forEach((a) => {
          if (!seenClasses.has(a.class_id)) {
            seenClasses.add(a.class_id);
            classes.push({ class_id: a.class_id, class_name: a.class_name });
          }
        });
        setTeacherClasses(classes);
        setTeacherSubjects(assignments.map((a) => ({
          class_id: a.class_id, subject_id: a.subject_id,
          subject_name: a.subject_name, id: a.subject_id,
        })));
      })
      .catch(() => { setTeacherClasses([]); setTeacherSubjects([]); });
  };

  useEffect(() => {
    dispatch(fetchLmsSchoolClasses(schoolId));
    dispatch(fetchLmsClassSubjects({ school_id: schoolId }));

    const tid = userData?.teacher_id;
    if (tid) { setupTeacher(tid); }
    else if (adminId) {
      lmsGetTeacherByUserId(adminId)
        .then((res) => { if (res?.data?.id) setupTeacher(res.data.id); else { setTeacherClasses([]); setTeacherSubjects([]); } })
        .catch(() => { setTeacherClasses([]); setTeacherSubjects([]); });
    } else {
      setTeacherClasses([]);
      setTeacherSubjects([]);
    }
  }, [adminId]);

  useEffect(() => {
    if (teacherClasses !== null) loadExams();
  }, [teacherClasses, schoolId, classFilter]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await lmsDeleteExam(confirmDel.id);
      toast.success("Exam deleted");
      setConfirmDel(null);
      loadExams();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  const filtered = exams.filter((e) => {
    const matchSearch = !search ||
      (e.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.class_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.subject_name || "").toLowerCase().includes(search.toLowerCase());
    const matchClass  = !classFilter || String(e.class_id) === String(classFilter);
    const matchBatch  = !batchFilter || String(e.batch_id) === String(batchFilter);
    const matchType   = !typeFilter  || e.exam_type === typeFilter;
    return matchSearch && matchClass && matchBatch && matchType;
  });

  const classesForModal  = (teacherClasses  && teacherClasses.length  > 0) ? teacherClasses  : schoolClasses;
  const subjectsForModal = (teacherSubjects && teacherSubjects.length > 0) ? teacherSubjects : classSubjects;

  const upcomingCount = exams.filter((e) => e.exam_date && new Date(e.exam_date) >= new Date()).length;

  return (
    <div className="lms-content-inner">
      {/* Header card */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />
        <div style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12,
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <ClipboardList size={22} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>My Exams</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              Create and manage exams for your classes
            </div>
          </div>

          <div style={{ display: "flex", gap: 20, flexShrink: 0 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#4f46e5" }}>{exams.length}</div>
              <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>TOTAL</div>
            </div>
            {upcomingCount > 0 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>{upcomingCount}</div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>UPCOMING</div>
              </div>
            )}
          </div>

          <button onClick={() => { setEditExam(null); setShowModal(true); }} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "10px 18px",
            borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            <Plus size={15} /> Create Exam
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="crs-filters" style={{ marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div className="crs-search-wrap" style={{ flex: 1, minWidth: 180 }}>
          <Search size={14} />
          <input placeholder="Search exams..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="crs-filter-select" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
          <option value="">All Classes</option>
          {(teacherClasses && teacherClasses.length > 0 ? teacherClasses : schoolClasses).map((c) => (
            <option key={c.class_id || c.id} value={c.class_id || c.id}>{c.class_name || c.name}</option>
          ))}
        </select>
        <select className="crs-filter-select" value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)}
          disabled={!classFilter} style={{ opacity: classFilter ? 1 : 0.5 }}>
          <option value="">All Batches</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>{b.name || b.batch_name}</option>
          ))}
        </select>
        <select className="crs-filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {["unit_test","half_yearly","annual","mock","practice"].map((t) => (
            <option key={t} value={t}>{t.replace("_"," ").replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
        <button onClick={loadExams} style={{
          padding: "8px 14px", borderRadius: 8, border: "1.5px solid #e5e7eb",
          background: "#fff", color: "#6b7280", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5, fontSize: 13, flexShrink: 0,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader size={28} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
        }}>
          <ClipboardList size={48} color="#e5e7eb" style={{ margin: "0 auto 16px" }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>No exams found</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
            Create your first exam to get started.
          </div>
          <button onClick={() => { setEditExam(null); setShowModal(true); }} style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Plus size={14} /> Create First Exam
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {filtered.map((exam) => (
            <ExamCard
              key={exam.id}
              exam={exam}
              onEdit={(e) => { setEditExam(e); setShowModal(true); }}
              onDelete={setConfirmDel}
              onView={setViewExam}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ExamModal
          exam={editExam}
          schoolId={schoolId}
          classes={classesForModal}
          classSubjects={subjectsForModal}
          onClose={() => { setShowModal(false); setEditExam(null); }}
          onSaved={loadExams}
        />
      )}
      {viewExam && (
        <ExamDetailDrawer
          exam={viewExam}
          schoolId={schoolId}
          onClose={() => setViewExam(null)}
          onNavigate={(path) => { setViewExam(null); nav(path.replace("/lms/", "/teacher-lms/")); }}
        />
      )}
      {confirmDel && (
        <div className="ms2-modal-overlay" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 14, padding: "32px 28px", width: 380,
            maxWidth: "92vw", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete Exam?</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
              Delete <strong>{confirmDel.name}</strong>? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setConfirmDel(null)} className="crs-btn-cancel">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{
                padding: "9px 22px", borderRadius: 8, border: "none",
                background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                {deleting ? <Loader size={13} className="ms2-spin" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
